import React, { useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Bell } from "lucide-react";
import { useWebSocket } from "../hooks/useWebSocket";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { fetchUserNotificationsByUser, incrementUnreadCount } from "../redux/Slices/UserNotificationSlice";
import { NotificationStatusEnum, NotificationTypeEnum } from "../modules/shared/model";
import { getUserById } from "../redux/Actions/AuthAction";

// Global flag to ensure only one instance is active
let activeInstanceId: string | null = null;
let instanceCounter = 0;

/**
 * WebSocketNotificationListener Component
 * 
 * This component listens for "notification" events from the WebSocket connection
 * and displays toast notifications when new notifications are received.
 * 
 * Features:
 * - Listens for WebSocket "notification" events
 * - Shows toast notification with the message
 * - On click, navigates to /notifications route
 * - If already on /notifications, fetches latest notifications
 * - Uses singleton pattern to ensure only one active instance
 */
const WebSocketNotificationListener: React.FC = () => {
  // Generate unique instance ID
  const instanceIdRef = useRef<string>(`listener-${++instanceCounter}-${Date.now()}`);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  
  // Track if this instance is active
  const isActiveInstanceRef = useRef(false);
  
  // Track shown notifications to prevent duplicates
  const shownNotificationsRef = useRef<Set<string>>(new Set());
  const lastUserSyncRef = useRef<number>(0);

  // Singleton pattern - ensure only one instance is active
  useEffect(() => {
    const myInstanceId = instanceIdRef.current;
    
    if (activeInstanceId === null) {
      // No active instance, make this one active
      activeInstanceId = myInstanceId;
      isActiveInstanceRef.current = true;
      console.log(`✅ [WebSocketNotificationListener] Instance ${myInstanceId} is now ACTIVE`);
    } else if (activeInstanceId !== myInstanceId) {
      // Another instance is already active
      isActiveInstanceRef.current = false;
      console.log(`⚠️ [WebSocketNotificationListener] Instance ${myInstanceId} is INACTIVE (active: ${activeInstanceId})`);
    }

    return () => {
      // Only clear the active instance if this instance was the active one
      if (activeInstanceId === myInstanceId) {
        activeInstanceId = null;
        isActiveInstanceRef.current = false;
        console.log(`🔻 [WebSocketNotificationListener] Instance ${myInstanceId} unmounted and cleared`);
      }
    };
  }, []);

  // Handle notification click - navigate to notifications page
  const handleNotificationClick = useCallback(() => {
    // Check if already on notifications page
    if (location.pathname === "/notifications" || location.pathname === "/international/notifications") {
      if (user?.id) {
        const queryParams: Record<string, string | number | boolean> = {
          page: 1,
          limit: 10,
          sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
        };
        
        dispatch(fetchUserNotificationsByUser({
          userId: user.id,
          query: queryParams
        }));
      }
    } else {
      // Navigate to notifications page
      navigate("/notifications");
    }
  }, [location.pathname, navigate, user?.id, dispatch]);

  // Store latest values in refs to avoid recreating the callback
  const handleNotificationClickRef = useRef(handleNotificationClick);
  const locationRef = useRef(location);
  const userIdRef = useRef(user?.id);
  const dispatchRef = useRef(dispatch);

  // Update refs when values change
  useEffect(() => {
    handleNotificationClickRef.current = handleNotificationClick;
    locationRef.current = location;
    userIdRef.current = user?.id;
    dispatchRef.current = dispatch;
  }, [handleNotificationClick, location, user?.id, dispatch]);

  // Handle incoming messages and filter for notifications
  // Use stable callback that doesn't change on every render
  const handleMessage = useCallback((message: any) => {
    // Only process if this is the active instance
    if (!isActiveInstanceRef.current) {
      console.log(`⏭️ [WebSocketNotificationListener] Skipping message - instance ${instanceIdRef.current} is not active`);
      return;
    }
    
    const rawType = message?.type || message?.detail?.type || message?.event || message?.detail?.event;
    const normalizedType = typeof rawType === "string" ? rawType.toLowerCase() : "";

    if (["user_permissions_updated", "user_profile_updated", "user_updated", "user_access_updated"].includes(normalizedType)) {
      const targetId = message?.detail?.userId ?? message?.detail?.id ?? message?.userId ?? message?.detail?.targetUserId;
      if (targetId && userIdRef.current && String(targetId) === String(userIdRef.current)) {
        const now = Date.now();
        if (now - lastUserSyncRef.current > 1000) {
          lastUserSyncRef.current = now;
          console.log("🔄 [WebSocketNotificationListener] Refreshing authenticated user after permissions update");
          dispatchRef.current(getUserById({ id: String(targetId), isShowLoader: false }));
        }
      }
    }

    // Check if this is a notification event
    console.log(`📨 [WebSocketNotificationListener] Instance ${instanceIdRef.current} received message:`, message);
    if (message.type === "notification" || message.type === "NOTIFICATION") {
      
      const notificationData = message.detail || message;
      
      // Check for translation keys first, then fall back to English
      const title = notificationData.titleKey 
        ? t(notificationData.titleKey, notificationData.messageParams || {})
        : (notificationData.title || "New Notification");
      
      const messageText = notificationData.messageKey
        ? t(notificationData.messageKey, notificationData.messageParams || {})
        : (notificationData.message || "You have received a new notification");
      
      // Create a more robust unique ID for this notification
      // Use multiple factors to ensure uniqueness
      const idParts = [
        notificationData.id,
        title,
        messageText,
        notificationData.createdAt,
        notificationData.timestamp
      ].filter(Boolean).join("-");
      
      const notificationId = notificationData.id || `notif-${idParts}`;
      const toastId = `toast-${notificationId}`;
      
      console.log("🔍 [WebSocketNotificationListener] Processing notification:", {
        notificationId,
        toastId,
        alreadyShown: shownNotificationsRef.current.has(notificationId),
        toastActive: toast.isActive(toastId),
        shownCount: shownNotificationsRef.current.size
      });
      
      // Check if this notification was already shown or if toast is already active
      if (shownNotificationsRef.current.has(notificationId) || toast.isActive(toastId)) {
        console.log("🚫 [WebSocketNotificationListener] Duplicate notification prevented:", notificationId);
        return;
      }
      
      // Mark this notification as shown
      shownNotificationsRef.current.add(notificationId);
      console.log("✅ [WebSocketNotificationListener] Showing notification:", notificationId);
      
      // Clean up old entries after 30 seconds to prevent memory leak
      setTimeout(() => {
        shownNotificationsRef.current.delete(notificationId);
      }, 30000);
      
      // Check notification type
      const notificationType = notificationData.type?.id || notificationData.typeId;
      const typeId = Number(notificationType);
      
      console.log("🔍 [WebSocketNotificationListener] Notification type:", typeId, {
        isAnnouncement: typeId === NotificationTypeEnum.Announcement,
        isMarketing: typeId === NotificationTypeEnum.Marketing,
        isPopup: typeId === NotificationTypeEnum.Popup,
        rawType: notificationType,
        NotificationTypeEnum
      });
      
      // Type 5 = Announcement - show announcement dialog + toast
      if (typeId === NotificationTypeEnum.Announcement) {
        // Show Announcement Dialog
        console.log("🎯 [WebSocketNotificationListener] Showing announcement dialog for notification:", notificationId);
        
        // Parse announcement content from message field if it's JSON
        let announcementTitle = title;
        let announcementContent = messageText;
        
        try {
          const parsedMessage = JSON.parse(messageText);
          if (parsedMessage.announcementTitle) {
            announcementTitle = parsedMessage.announcementTitle;
          }
          if (parsedMessage.announcementContent) {
            announcementContent = parsedMessage.announcementContent;
          }
        } catch (e) {
          // If not JSON, use message as is
          announcementContent = messageText;
        }
        
        // Show toast notification for announcement
        toast.info(
          <div className="flex items-start gap-3 cursor-pointer">
            <Bell className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-gray-900">{ t("notifications.newAnnouncement")}</p>
            <p className="text-sm text-gray-600 mt-1">{announcementTitle}</p>
            <p className="text-xs text-indigo-600 mt-2">{t("notifications.clickToViewAnnouncementDetails")}</p>
          </div>
          </div>,
          {
            toastId: toastId, // Unique ID prevents duplicate toasts
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: "cursor-pointer",
            style: {
              cursor: "pointer",
            },
            onClick: () => {
              // Trigger announcement popup when toast is clicked
              const popupEvent = new CustomEvent("showNotificationPopup", {
                detail: {
                  id: notificationId,
                  notificationId: notificationId,
                  title: announcementTitle, // Use parsed title
                  message: announcementContent, // Use parsed content
                  priority: "medium",
                  type: notificationData.type,
                  url: notificationData.url,
                  expiryTime: notificationData.expiryTime,
                  status: notificationData.status,
                  createdAt: notificationData.createdAt,
                  announcementTitle: announcementTitle,
                  announcementContent: announcementContent,
                }
              });
              window.dispatchEvent(popupEvent);
            },
          }
        );
        
        // Debug logging
        console.log("🔍 [WebSocketNotificationListener] Sending popup event with data:", {
          originalMessage: messageText,
          parsedTitle: announcementTitle,
          parsedContent: announcementContent,
          finalTitle: announcementTitle,
          finalMessage: announcementContent
        });
        
        // Also trigger the popup event for immediate display
        const popupEvent = new CustomEvent("showNotificationPopup", {
          detail: {
            id: notificationId,
            notificationId: notificationId,
            title: announcementTitle, // Use parsed title
            message: announcementContent, // Use parsed content
            priority: "medium",
            type: notificationData.type,
            url: notificationData.url,
            expiryTime: notificationData.expiryTime,
            status: notificationData.status,
            createdAt: notificationData.createdAt,
            announcementTitle: announcementTitle,
            announcementContent: announcementContent,
          }
        });
        window.dispatchEvent(popupEvent);
      } 
      // Type 6 = Marketing - show marketing popup dialog + toast
      else if (typeId === NotificationTypeEnum.Marketing) {
        // Show Marketing Popup Dialog
        console.log("🎯 [WebSocketNotificationListener] Showing marketing popup dialog for notification:", notificationId);
        
        // Parse marketing popup data from message field
        let popupData: any = {};
        
        try {
          popupData = JSON.parse(messageText);
          console.log("📦 [WebSocketNotificationListener] Parsed marketing popup data:", popupData);
        } catch (e) {
          console.error("❌ [WebSocketNotificationListener] Failed to parse marketing popup data:", e);
        }
        
        // Show toast notification for marketing popup
        toast.info(
          <div className="flex items-start gap-3 cursor-pointer">
            <Bell className="h-5 w-5 text-pink-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-gray-900">{t("notifications.marketingPopup")}</p>
            <p className="text-sm text-gray-600 mt-1">{popupData.contentTitle || title}</p>
            <p className="text-xs text-pink-600 mt-2">{t("notifications.clickToView")}</p>
          </div>
          </div>,
          {
            toastId: toastId, // Unique ID prevents duplicate toasts
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: "cursor-pointer",
            style: {
              cursor: "pointer",
            },
            onClick: () => {
              // Trigger marketing popup when toast is clicked
              const popupEvent = new CustomEvent("showNotificationPopup", {
                detail: {
                  id: notificationId,
                  notificationId: notificationId,
                  title: title,
                  message: messageText,
                  priority: "medium",
                  type: notificationData.type,
                  url: notificationData.url,
                  expiryTime: notificationData.expiryTime,
                  status: notificationData.status,
                  createdAt: notificationData.createdAt,
                  popupData: popupData,
                }
              });
              window.dispatchEvent(popupEvent);
            },
          }
        );
        
        console.log("📝 [WebSocketNotificationListener] Marketing popup data prepared:", {
          notificationId,
          messageText,
          originalMessage: messageText,
          parsedData: popupData,
        });
        
        // Also trigger the popup event for immediate display
        const popupEvent = new CustomEvent("showNotificationPopup", {
          detail: {
            id: notificationId,
            notificationId: notificationId,
            title: title,
            message: messageText,
            priority: "medium",
            type: notificationData.type,
            url: notificationData.url,
            expiryTime: notificationData.expiryTime,
            status: notificationData.status,
            createdAt: notificationData.createdAt,
            popupData: popupData,
          }
        });
        window.dispatchEvent(popupEvent);
      } 
      // Type 2 = Popup - show popup toast
      else if (typeId === NotificationTypeEnum.Popup) {
        // Show regular popup toast
        console.log("🎯 [WebSocketNotificationListener] Showing popup toast for notification:", notificationId);
        
        toast.info(
          <div 
            className="flex items-start gap-3 cursor-pointer"
            onClick={() => handleNotificationClickRef.current()}
          >
            <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{title}</p>
              <p className="text-sm text-gray-600 mt-1">{messageText}</p>
              <p className="text-xs text-blue-600 mt-2">{t("notifications.clickToView")}</p>
            </div>
          </div>,
          {
            toastId: toastId, // Unique ID prevents duplicate toasts
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: "cursor-pointer",
            style: {
              cursor: "pointer",
            },
            onClick: () => handleNotificationClickRef.current(),
          }
        );
      } else {
        // Display toast notification for non-popup types
        console.log("📢 [WebSocketNotificationListener] Showing toast for notification:", notificationId);
        
        toast.info(
          <div 
            className="flex items-start gap-3 cursor-pointer"
            onClick={() => handleNotificationClickRef.current()}
          >
            <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{title}</p>
              <p className="text-sm text-gray-600 mt-1">{messageText}</p>
              <p className="text-xs text-blue-600 mt-2">{t("notifications.clickToView")}</p>
            </div>
          </div>,
          {
            toastId: toastId, // Unique ID prevents duplicate toasts
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: "cursor-pointer",
            style: {
              cursor: "pointer",
            },
            onClick: () => handleNotificationClickRef.current(),
          }
        );
      }

      // Increment unread count
      dispatchRef.current(incrementUnreadCount());

      // If already on notifications page, fetch latest notifications
      if (locationRef.current.pathname === "/notifications" || 
          locationRef.current.pathname === "/international/notifications") {
        if (userIdRef.current) {
          const queryParams: Record<string, string | number | boolean> = {
            page: 1,
            limit: 10,
            sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
          };
          
          dispatchRef.current(fetchUserNotificationsByUser({
            userId: userIdRef.current,
            query: queryParams
          }));
        }
      }
    }
  }, []); // Empty dependency array - callback never changes

  // Memoize WebSocket options to prevent unnecessary re-registrations
  const webSocketOptions = useMemo(() => ({
    onMessage: handleMessage,
    onNotification: handleMessage,
    onConnect: () => {
      // WebSocket connected
    },
    onDisconnect: () => {
      // WebSocket disconnected
    },
    onError: (err: any) => {
      console.error("WebSocket Notification Listener error:", err);
    },
  }), [handleMessage]); // Only recreate if handleMessage changes (which it won't)

  // Initialize WebSocket connection with message listener
  const { isConnected, error } = useWebSocket(webSocketOptions);

  // This component doesn't render anything
  return null;
};

export default WebSocketNotificationListener;


